import { MuseError } from "@musejs/core";
//#region src/zip/crc32.ts
/** CRC-32 (IEEE 802.3), as required by the ZIP format. */
const TABLE = (() => {
	const table = /* @__PURE__ */ new Uint32Array(256);
	for (let i = 0; i < 256; i++) {
		let c = i;
		for (let k = 0; k < 8; k++) c = (c & 1) === 1 ? 3988292384 ^ c >>> 1 : c >>> 1;
		table[i] = c >>> 0;
	}
	return table;
})();
/** Continues a running CRC so an entry can be hashed chunk by chunk. */
function crc32Update(crc, data) {
	let c = crc ^ 4294967295;
	for (let i = 0; i < data.length; i++) c = TABLE[(c ^ data[i]) & 255] ^ c >>> 8;
	return (c ^ 4294967295) >>> 0;
}
function crc32(data) {
	return crc32Update(0, data);
}
//#endregion
//#region src/zip/reader.ts
const CENTRAL_SIGNATURE = 33639248;
const EOCD_SIGNATURE = 101010256;
const LOCAL_SIGNATURE = 67324752;
const METHOD_STORE = 0;
const EOCD_MIN_SIZE = 22;
/**
* Reads a ZIP archive produced by this library, or any writer using stored
* entries without ZIP64.
*
* Entries are returned as views into `bytes`, so reading a large session does
* not duplicate it in memory. Callers must not mutate the source buffer.
*/
function readZip(bytes, options = {}) {
	const verify = options.verifyChecksums ?? true;
	const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
	const eocd = findEndOfCentralDirectory(bytes, view);
	const entryCount = view.getUint16(eocd + 10, true);
	let cursor = view.getUint32(eocd + 16, true);
	const entries = /* @__PURE__ */ new Map();
	const decoder = new TextDecoder("utf-8", { fatal: false });
	for (let i = 0; i < entryCount; i++) {
		if (cursor + 46 > bytes.length || view.getUint32(cursor, true) !== CENTRAL_SIGNATURE) throw new MuseError("MALFORMED_PACKET", `Central directory entry ${i} is missing or corrupt`);
		const method = view.getUint16(cursor + 10, true);
		const expectedCrc = view.getUint32(cursor + 16, true);
		const size = view.getUint32(cursor + 24, true);
		const nameLength = view.getUint16(cursor + 28, true);
		const extraLength = view.getUint16(cursor + 30, true);
		const commentLength = view.getUint16(cursor + 32, true);
		const localOffset = view.getUint32(cursor + 42, true);
		const name = decoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
		if (method !== METHOD_STORE) throw new MuseError("MALFORMED_PACKET", `Entry "${name}" uses compression method ${method}; only stored entries are supported`, { details: {
			entry: name,
			method
		} });
		if (localOffset + 30 > bytes.length || view.getUint32(localOffset, true) !== LOCAL_SIGNATURE) throw new MuseError("MALFORMED_PACKET", `Entry "${name}" has no valid local header`);
		const localNameLength = view.getUint16(localOffset + 26, true);
		const localExtraLength = view.getUint16(localOffset + 28, true);
		const dataStart = localOffset + 30 + localNameLength + localExtraLength;
		if (dataStart + size > bytes.length) throw new MuseError("MALFORMED_PACKET", `Entry "${name}" extends past the end of the archive`);
		const data = bytes.subarray(dataStart, dataStart + size);
		if (verify && crc32(data) !== expectedCrc) throw new MuseError("MALFORMED_PACKET", `Entry "${name}" failed its CRC-32 check`, { details: {
			entry: name,
			size
		} });
		if (entries.has(name)) throw new MuseError("MALFORMED_PACKET", `Archive contains "${name}" twice`, { details: { entry: name } });
		entries.set(name, data);
		cursor += 46 + nameLength + extraLength + commentLength;
	}
	return { entries };
}
/**
* Locates the end-of-central-directory record.
*
* It sits at the end of the file, but may be followed by a comment of up to
* 64 KiB, so the signature has to be searched for backwards.
*/
function findEndOfCentralDirectory(bytes, view) {
	if (bytes.length < EOCD_MIN_SIZE) throw new MuseError("MALFORMED_PACKET", `Not a ZIP archive: only ${bytes.length} bytes`);
	const earliest = Math.max(0, bytes.length - EOCD_MIN_SIZE - 65535);
	for (let i = bytes.length - EOCD_MIN_SIZE; i >= earliest; i--) if (view.getUint32(i, true) === EOCD_SIGNATURE) return i;
	throw new MuseError("MALFORMED_PACKET", "Not a ZIP archive: no end-of-central-directory record");
}
//#endregion
//#region src/hash/sha256.ts
/**
* Streaming SHA-256 (FIPS 180-4).
*
* Hand-rolled rather than delegating to Web Crypto for one reason: `crypto.subtle.digest`
* is one-shot, so hashing a session would mean concatenating every chunk into a
* single buffer — exactly the allocation the chunked frame log exists to avoid.
* A streaming digest hashes a multi-hundred-megabyte session in constant memory.
*
* Verified against the NIST test vectors in the accompanying tests.
*/
const K = new Uint32Array([
	1116352408,
	1899447441,
	3049323471,
	3921009573,
	961987163,
	1508970993,
	2453635748,
	2870763221,
	3624381080,
	310598401,
	607225278,
	1426881987,
	1925078388,
	2162078206,
	2614888103,
	3248222580,
	3835390401,
	4022224774,
	264347078,
	604807628,
	770255983,
	1249150122,
	1555081692,
	1996064986,
	2554220882,
	2821834349,
	2952996808,
	3210313671,
	3336571891,
	3584528711,
	113926993,
	338241895,
	666307205,
	773529912,
	1294757372,
	1396182291,
	1695183700,
	1986661051,
	2177026350,
	2456956037,
	2730485921,
	2820302411,
	3259730800,
	3345764771,
	3516065817,
	3600352804,
	4094571909,
	275423344,
	430227734,
	506948616,
	659060556,
	883997877,
	958139571,
	1322822218,
	1537002063,
	1747873779,
	1955562222,
	2024104815,
	2227730452,
	2361852424,
	2428436474,
	2756734187,
	3204031479,
	3329325298
]);
var Sha256 = class {
	#state = new Uint32Array([
		1779033703,
		3144134277,
		1013904242,
		2773480762,
		1359893119,
		2600822924,
		528734635,
		1541459225
	]);
	#block = /* @__PURE__ */ new Uint8Array(64);
	#w = /* @__PURE__ */ new Uint32Array(64);
	#blockLength = 0;
	#totalLength = 0;
	#finished = false;
	update(data) {
		if (this.#finished) throw new Error("Sha256 already finalised");
		this.#totalLength += data.length;
		let offset = 0;
		if (this.#blockLength > 0) {
			const need = Math.min(64 - this.#blockLength, data.length);
			this.#block.set(data.subarray(0, need), this.#blockLength);
			this.#blockLength += need;
			offset = need;
			if (this.#blockLength === 64) {
				this.#compress(this.#block, 0);
				this.#blockLength = 0;
			}
		}
		while (offset + 64 <= data.length) {
			this.#compress(data, offset);
			offset += 64;
		}
		if (offset < data.length) {
			this.#block.set(data.subarray(offset), 0);
			this.#blockLength = data.length - offset;
		}
		return this;
	}
	digest() {
		if (this.#finished) throw new Error("Sha256 already finalised");
		this.#finished = true;
		const bitLength = this.#totalLength * 8;
		this.#block[this.#blockLength++] = 128;
		if (this.#blockLength > 56) {
			this.#block.fill(0, this.#blockLength);
			this.#compress(this.#block, 0);
			this.#blockLength = 0;
		}
		this.#block.fill(0, this.#blockLength, 56);
		const view = new DataView(this.#block.buffer);
		view.setUint32(56, Math.floor(bitLength / 4294967296), false);
		view.setUint32(60, bitLength >>> 0, false);
		this.#compress(this.#block, 0);
		const out = /* @__PURE__ */ new Uint8Array(32);
		const outView = new DataView(out.buffer);
		for (let i = 0; i < 8; i++) outView.setUint32(i * 4, this.#state[i], false);
		return out;
	}
	hex() {
		return [...this.digest()].map((b) => b.toString(16).padStart(2, "0")).join("");
	}
	#compress(data, offset) {
		const w = this.#w;
		for (let i = 0; i < 16; i++) {
			const o = offset + i * 4;
			w[i] = data[o] << 24 | data[o + 1] << 16 | data[o + 2] << 8 | data[o + 3];
		}
		for (let i = 16; i < 64; i++) {
			const x = w[i - 15];
			const y = w[i - 2];
			const s0 = (x >>> 7 | x << 25) ^ (x >>> 18 | x << 14) ^ x >>> 3;
			const s1 = (y >>> 17 | y << 15) ^ (y >>> 19 | y << 13) ^ y >>> 10;
			w[i] = w[i - 16] + s0 + w[i - 7] + s1 >>> 0;
		}
		let [a, b, c, d, e, f, g, h] = this.#state;
		for (let i = 0; i < 64; i++) {
			const S1 = (e >>> 6 | e << 26) ^ (e >>> 11 | e << 21) ^ (e >>> 25 | e << 7);
			const ch = e & f ^ ~e & g;
			const t1 = h + S1 + ch + K[i] + w[i] >>> 0;
			const t2 = ((a >>> 2 | a << 30) ^ (a >>> 13 | a << 19) ^ (a >>> 22 | a << 10)) + (a & b ^ a & c ^ b & c) >>> 0;
			h = g;
			g = f;
			f = e;
			e = d + t1 >>> 0;
			d = c;
			c = b;
			b = a;
			a = t1 + t2 >>> 0;
		}
		const s = this.#state;
		s[0] = s[0] + a >>> 0;
		s[1] = s[1] + b >>> 0;
		s[2] = s[2] + c >>> 0;
		s[3] = s[3] + d >>> 0;
		s[4] = s[4] + e >>> 0;
		s[5] = s[5] + f >>> 0;
		s[6] = s[6] + g >>> 0;
		s[7] = s[7] + h >>> 0;
	}
};
/** Hashes a sequence of chunks without concatenating them. */
function sha256Hex(chunks) {
	const hash = new Sha256();
	for (const chunk of chunks) hash.update(chunk);
	return hash.hex();
}
//#endregion
//#region src/session/frame-log.ts
/**
* Binary log of raw BLE notifications, exactly as they arrived.
*
* This is the primary record. Everything else in a session — decoded samples,
* derived signals, exports — is reconstructible from it, which is what makes a
* session reproducible: fix a decoder bug, re-run, and the corrected result
* follows from the same evidence.
*
* Record layout (little-endian):
* ```
* [0..3]   u32  payload length
* [4..11]  f64  arrival time, microseconds since the Unix epoch
* [12..13] u16  characteristic index, into the manifest's characteristic table
* [14..]        payload
* ```
*
* The characteristic is an index rather than a UUID string because a session
* holds hundreds of thousands of frames from a handful of characteristics;
* repeating a 36-character UUID per frame would dominate the file.
*
* Arrival time is a float64 rather than an integer: microsecond timestamps run
* to ~1.8e15, comfortably inside exact integer range, and float64 avoids the
* split-word arithmetic a u64 would need.
*/
const FRAME_RECORD_HEADER_SIZE = 14;
/**
* Appends frames into growable chunks.
*
* Chunked rather than one big buffer so a long session neither reallocates
* repeatedly nor needs its final size known in advance.
*/
var FrameLogWriter = class {
	#chunks = [];
	#offsets = [];
	#chunkSize;
	#current;
	#used = 0;
	#totalBytes = 0;
	#count = 0;
	constructor(options = {}) {
		this.#chunkSize = options.chunkSize ?? 1 << 20;
		this.#current = new Uint8Array(this.#chunkSize);
	}
	get frameCount() {
		return this.#count;
	}
	get byteLength() {
		return this.#totalBytes;
	}
	append(frame) {
		const size = 14 + frame.payload.length;
		if (size > this.#chunkSize) {
			this.#flush();
			const solo = new Uint8Array(size);
			writeRecord(solo, 0, frame);
			this.#offsets.push(this.#totalBytes);
			this.#chunks.push(solo);
			this.#totalBytes += size;
			this.#count++;
			return;
		}
		if (this.#used + size > this.#chunkSize) this.#flush();
		writeRecord(this.#current, this.#used, frame);
		this.#offsets.push(this.#totalBytes);
		this.#used += size;
		this.#totalBytes += size;
		this.#count++;
	}
	/** Frame data, as chunks to hand straight to the ZIP writer. */
	chunks() {
		this.#flush();
		return this.#chunks;
	}
	/** Byte offset of every record, for random access without a full scan. */
	index() {
		const out = new Uint8Array(this.#offsets.length * 4);
		const view = new DataView(out.buffer);
		this.#offsets.forEach((offset, i) => view.setUint32(i * 4, offset, true));
		return out;
	}
	#flush() {
		if (this.#used === 0) return;
		this.#chunks.push(this.#current.subarray(0, this.#used));
		this.#current = new Uint8Array(this.#chunkSize);
		this.#used = 0;
	}
};
function writeRecord(target, offset, frame) {
	const view = new DataView(target.buffer, target.byteOffset, target.byteLength);
	view.setUint32(offset, frame.payload.length, true);
	view.setFloat64(offset + 4, frame.arrivalTime, true);
	view.setUint16(offset + 12, frame.characteristicIndex, true);
	target.set(frame.payload, offset + 14);
}
/**
* Walks a frame log.
*
* Yields views into `bytes` rather than copies, so iterating a large session
* allocates nothing per frame. Consumers that keep a payload must copy it.
*/
function* readFrameLog(bytes) {
	const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
	let offset = 0;
	let index = 0;
	while (offset < bytes.length) {
		if (offset + 14 > bytes.length) throw new MuseError("MALFORMED_PACKET", `Frame log truncated: ${bytes.length - offset} byte(s) after frame ${index}`, { details: {
			frameIndex: index,
			offset
		} });
		const length = view.getUint32(offset, true);
		const end = offset + 14 + length;
		if (end > bytes.length) throw new MuseError("MALFORMED_PACKET", `Frame ${index} claims ${length} bytes but the log ends first`, { details: {
			frameIndex: index,
			length
		} });
		yield {
			arrivalTime: view.getFloat64(offset + 4, true),
			characteristicIndex: view.getUint16(offset + 12, true),
			payload: bytes.subarray(offset + 14, end)
		};
		offset = end;
		index++;
	}
}
/** Counts frames without materialising them. */
function countFrames(bytes) {
	let count = 0;
	for (const _ of readFrameLog(bytes)) count++;
	return count;
}
//#endregion
//#region src/session/manifest.ts
/**
* Version of the `.musez` layout.
*
* A reader accepts every version it knows and refuses anything newer with a
* clear message, rather than guessing and mis-parsing. Bump this only for
* changes a previous reader could not handle correctly.
*/
const SESSION_FORMAT_VERSION = 1;
/**
* Validates a parsed manifest.
*
* Deliberately strict: a session file is evidence, and a reader that shrugs at
* a missing field will produce results nobody can defend later.
*/
function parseManifest(json) {
	if (typeof json !== "object" || json === null) throw new MuseError("MALFORMED_PACKET", "Session manifest is not an object");
	const m = json;
	const version = m["formatVersion"];
	if (typeof version !== "number" || !Number.isInteger(version)) throw new MuseError("MALFORMED_PACKET", "Session manifest has no integer formatVersion");
	if (version > 1) throw new MuseError("UNSUPPORTED_DEVICE", `Session uses format version ${version}; this build understands up to 1. Upgrade @musejs/recording.`, { details: {
		found: version,
		supported: 1
	} });
	const characteristics = m["characteristics"];
	if (!Array.isArray(characteristics) || characteristics.some((c) => typeof c !== "string")) throw new MuseError("MALFORMED_PACKET", "Session manifest has no characteristic table");
	for (const key of ["startedAt", "endedAt"]) if (typeof m[key] !== "number") throw new MuseError("MALFORMED_PACKET", `Session manifest field "${key}" is not a number`);
	if (typeof m["device"] !== "object" || m["device"] === null) throw new MuseError("MALFORMED_PACKET", "Session manifest has no device block");
	const device = m["device"];
	if (typeof device["id"] !== "string") throw new MuseError("MALFORMED_PACKET", "Session manifest device has no string \"id\"");
	if (device["mtu"] !== null && device["mtu"] !== void 0 && typeof device["mtu"] !== "number") throw new MuseError("MALFORMED_PACKET", "Session manifest device \"mtu\" is not a number");
	const integrity = m["integrity"];
	if (typeof integrity !== "object" || integrity === null) throw new MuseError("MALFORMED_PACKET", "Session manifest has no integrity block");
	const i = integrity;
	for (const key of ["rawFrameCount", "rawByteLength"]) {
		const value = i[key];
		if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw new MuseError("MALFORMED_PACKET", `Session manifest integrity "${key}" is not a count`);
	}
	if (i["rawSha256"] !== null && typeof i["rawSha256"] !== "string") throw new MuseError("MALFORMED_PACKET", "Session manifest integrity \"rawSha256\" is not a string");
	const preset = m["preset"];
	if (typeof preset !== "object" || preset === null) throw new MuseError("MALFORMED_PACKET", "Session manifest has no preset block");
	const p = preset;
	if (p["requested"] !== null && typeof p["requested"] !== "string") throw new MuseError("MALFORMED_PACKET", "Session manifest preset \"requested\" is not a string");
	const observed = p["observedSignals"];
	if (!Array.isArray(observed) || observed.some((o) => typeof o !== "string")) throw new MuseError("MALFORMED_PACKET", "Session manifest preset \"observedSignals\" is not a list");
	return json;
}
//#endregion
//#region src/session/reader.ts
/** A `.musez` session opened for reading. */
var Session = class {
	manifest;
	events;
	#rawFrames;
	constructor(manifest, events, rawFrames) {
		this.manifest = manifest;
		this.events = events;
		this.#rawFrames = rawFrames;
	}
	get frameCount() {
		return this.manifest.integrity.rawFrameCount;
	}
	/** Wall-clock span of the recording, in microseconds. */
	get durationUs() {
		return this.manifest.endedAt - this.manifest.startedAt;
	}
	/** Every notification, in recorded order. */
	*frames() {
		const uuids = this.manifest.characteristics;
		for (const frame of readFrameLog(this.#rawFrames)) {
			const characteristic = uuids[frame.characteristicIndex];
			if (characteristic === void 0) throw new MuseError("MALFORMED_PACKET", `Frame references characteristic index ${frame.characteristicIndex}, but the manifest lists ${uuids.length}`);
			yield {
				characteristic,
				arrivalTime: frame.arrivalTime,
				payload: frame.payload
			};
		}
	}
	/** Only the notifications from one characteristic. */
	*framesFor(characteristic) {
		for (const frame of this.frames()) if (frame.characteristic === characteristic) yield frame;
	}
	/**
	* Re-hashes the raw log and compares against the manifest.
	*
	* Worth running before analysis: it is the difference between "this file
	* parsed" and "this file is the one that was recorded".
	*/
	verifyIntegrity() {
		const actualSha256 = sha256Hex([this.#rawFrames]);
		let actualFrameCount = 0;
		for (const _ of readFrameLog(this.#rawFrames)) actualFrameCount++;
		const expectedSha256 = this.manifest.integrity.rawSha256;
		const expectedFrameCount = this.manifest.integrity.rawFrameCount;
		return {
			ok: (expectedSha256 === null || expectedSha256 === actualSha256) && expectedFrameCount === actualFrameCount,
			expectedSha256,
			actualSha256,
			expectedFrameCount,
			actualFrameCount
		};
	}
	/** The raw log, for callers that want to re-serialise or hash it themselves. */
	rawFrameBytes() {
		return this.#rawFrames;
	}
};
function readSession(bytes, options = {}) {
	const { entries } = readZip(bytes, options);
	const manifestBytes = required(entries, "manifest.json");
	const decoder = new TextDecoder();
	let manifestJson;
	try {
		manifestJson = JSON.parse(decoder.decode(manifestBytes));
	} catch (cause) {
		throw MuseError.wrap(cause, "MALFORMED_PACKET", "Session manifest is not valid JSON");
	}
	const manifest = parseManifest(manifestJson);
	const rawFrames = required(entries, "raw/frames.bin");
	const session = new Session(manifest, parseEvents(entries.get("events.jsonl"), decoder), rawFrames);
	if (options.requireIntegrity === true) {
		const report = session.verifyIntegrity();
		if (!report.ok) throw new MuseError("MALFORMED_PACKET", "Session failed its integrity check", { details: { ...report } });
	}
	return session;
}
function required(entries, name) {
	const value = entries.get(name);
	if (value === void 0) throw new MuseError("MALFORMED_PACKET", `Session is missing "${name}"`, { details: { present: [...entries.keys()] } });
	return value;
}
function parseEvents(bytes, decoder) {
	if (bytes === void 0 || bytes.length === 0) return [];
	const events = [];
	const text = decoder.decode(bytes);
	let lineNumber = 0;
	for (const line of text.split("\n")) {
		lineNumber++;
		if (line.trim().length === 0) continue;
		try {
			events.push(JSON.parse(line));
		} catch (cause) {
			throw MuseError.wrap(cause, "MALFORMED_PACKET", `events.jsonl line ${lineNumber} is not valid JSON`);
		}
	}
	return events;
}
//#endregion
export { FRAME_RECORD_HEADER_SIZE as a, readFrameLog as c, readZip as d, crc32 as f, parseManifest as i, Sha256 as l, readSession as n, FrameLogWriter as o, crc32Update as p, SESSION_FORMAT_VERSION as r, countFrames as s, Session as t, sha256Hex as u };

//# sourceMappingURL=reader-Cd74Mhmv.js.map